import GroupsSearchBar from "./GroupsSearchBar";
import { MainContentWrapper } from "../../../components/common/Common.styles";
import { useEffect, useRef, useState } from "react";
import { setProcessingRequest, setResponseMessage, setResponseValue } from "../../../store/reducers/tableManagementReducer";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { useResponseValue } from "../../../store/selectors/tableManagementSelector";
import { useServerSidePagination } from "../../../hooks/PaginationHooks";
import { ServerSidePagination } from "../../../components/common/table/ServerSidePagination";
import { fetchActionModules } from "../../../store/thunk/actionsModulesThunk";
import { createGroupThunk, deleteGroupThunk, editGroupThunk, fetchGroupPageFiltersThunk, fetchPaginatedGroupsThunk } from "../../../store/thunk/groupsThunk";
import { GroupItem, GroupRequest } from "../../../types/groupItem";
import EditGroupDialog from "./EditGroupDialog";
import GroupsTable from "./GroupsTable";
import { t } from "i18next";
import { mapThunkTypeToResponseValue } from "../../../common/logic/mapThunkTypeToResponseValue";
import { fetchShopModulesThunk } from "../../../store/thunk/shopModulesThunk";
import { GroupFilterParameters, GroupsPageFiltersResponse } from "../../../types/groupPageFilters";
import { keyValueStorage } from "../../../common/logic/KeyValueStore";

export default function GroupsBlock() {
  const dispatch = useAppDispatch();
  const responseValue = useResponseValue();
  const [isNewGroupDialogOpen, setIsNewGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupItem | undefined>();

  const [filterOptions, setFilterOptions] = useState<GroupsPageFiltersResponse>({
    actionModules: [], serviceModules: [], shopModules: [],
  });

  const defaultFilters: GroupFilterParameters = {
    actionModuleIds: [], serviceModuleIds: [], shopModuleIds: [],
  };

  const [selectedFilters, setSelectedFilters] = useState<GroupFilterParameters>(() =>
    keyValueStorage.getObject<GroupFilterParameters>("groups_filters", defaultFilters)
  );

  const paginatedData = useServerSidePagination<GroupItem>(
    async (paginationParameters) => {
      const resultAction = await dispatch(fetchPaginatedGroupsThunk({
        paginationParams: paginationParameters,
        filters: selectedFilters,
      }));

      if (fetchPaginatedGroupsThunk.fulfilled.match(resultAction)) {
        return resultAction.payload;
      } else {
        return { items: [], totalCount: 0 };
      }
    },
    []
  );

  useEffect(() => {
    if (responseValue.includes("fulfilled")) {
      dispatch(setResponseValue(""))
      paginatedData.updateTableItems();
    }
  }, [responseValue, paginatedData]);

  useEffect(() => {
    dispatch(fetchActionModules());
    dispatch(fetchShopModulesThunk())
    dispatch(fetchGroupPageFiltersThunk()).then((action) => {
      if (fetchGroupPageFiltersThunk.fulfilled.match(action) && action.payload) {
        const options = action.payload;
        setFilterOptions(options);

        const validIds = {
          actionModule: new Set(options.actionModules.map(a => a.id)),
          serviceModule: new Set(options.serviceModules.map(s => s.id)),
          shopModule: new Set(options.shopModules.map(s => s.id)),
        };
        setSelectedFilters(prev => {
          const cleaned: GroupFilterParameters = {
            actionModuleIds: prev.actionModuleIds.filter(id => validIds.actionModule.has(id)),
            serviceModuleIds: prev.serviceModuleIds.filter(id => validIds.serviceModule.has(id)),
            shopModuleIds: prev.shopModuleIds.filter(id => validIds.shopModule.has(id)),
          };
          keyValueStorage.setObject("groups_filters", cleaned);
          return cleaned;
        });
      }
    });
  }, []);

  const filterDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleFilterChange = (filter: Partial<GroupFilterParameters>) => {
    setSelectedFilters(prev => {
      const updated = { ...prev, ...filter };
      keyValueStorage.setObject("groups_filters", updated);
      return updated;
    });
    clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      paginatedData.updateTableItems();
    }, 500);
  };

  const handleSaveOrCreateGroup = async (group: GroupRequest, id: number | undefined) => {
    dispatch(setProcessingRequest(true));

    if (id) {
      const res = await dispatch(editGroupThunk({group, id}));
      dispatch(setResponseValue(mapThunkTypeToResponseValue(res.type)));
      dispatch(setResponseMessage(t("dialogModals.itemUpdatedGroup")));
    } else {
      const res = await dispatch(createGroupThunk(group));
      dispatch(setResponseValue(mapThunkTypeToResponseValue(res.type)));
      dispatch(setResponseMessage(t("dialogModals.itemCreatedGroup")));
    }

    dispatch(setProcessingRequest(false));
  };

  const handleDeleteGroup = async (group: GroupItem) => {
    dispatch(setProcessingRequest(true));
    const res = await dispatch(deleteGroupThunk(group.id));
    dispatch(setResponseValue(mapThunkTypeToResponseValue(res.type)));
    dispatch(setResponseMessage(t("dialogModals.itemDeletedGroup")));
    dispatch(setProcessingRequest(false));
  };

  return (
    <MainContentWrapper>
      <GroupsSearchBar
        handleOnSearch={paginatedData.handleOnSearch}
        handleOnCreateGroup={() => setIsNewGroupDialogOpen(true)}
        filterOptions={filterOptions}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange} />

      <GroupsTable
        paginatedData={paginatedData}
        handleDeleteGroup={handleDeleteGroup}
        handleEditGroup={(group) => setEditingGroup(group)}
      />

      <ServerSidePagination paginatedData={paginatedData} />

      {
        isNewGroupDialogOpen && <EditGroupDialog
          onClose={() => setIsNewGroupDialogOpen(false)}
          onSave={handleSaveOrCreateGroup} />
      }
      {
        editingGroup && <EditGroupDialog
          onClose={() => setEditingGroup(undefined)}
          onSave={handleSaveOrCreateGroup}
          group={editingGroup}
        />
      }

    </MainContentWrapper>
  );
};
